import { Component, Input, Output, EventEmitter, signal, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

type FooterMode = 'bridge' | 'default';

@Component({
  selector: 'app-footer-nav',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  templateUrl: './footer-nav.component.html'
})
export class FooterNavComponent implements OnInit, OnDestroy {
  /** Bleu en bridge, violet ailleurs */
  @Input() mode: FooterMode = 'default';
  /** Masquer le footer si le clavier mobile est ouvert */
  @Input() hideOnKeyboard = true;

  /** Événement quand on clique sur "Paramètres" (ouvrira <app-settings> dans le parent) */
  @Output() settingsClick = new EventEmitter<void>();
  @Output() infoClick = new EventEmitter<void>();

  /** true quand on est en viewport "mobile" (footer caché en desktop) */
  isMobile = signal<boolean>(true);
  /** true si le clavier est détecté (via VisualViewport) */
  kbOpen = signal<boolean>(false);

  private vv?: VisualViewport;
  private baseVH = 0;
  private isBrowser = false;

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    if (!this.isBrowser) return; // ⛑️ Skip total côté SSR

    // mobile-only
    const updateMobile = () => this.isMobile.set(window.innerWidth < 1024);
    updateMobile();
    window.addEventListener('resize', updateMobile, { passive: true });

    // keyboard detection (si supporté)
    if (this.hideOnKeyboard && 'visualViewport' in window) {
      this.vv = window.visualViewport as VisualViewport;
      this.baseVH = this.vv.height;
      const onVv = () => {
        const h = this.vv!.height;
        // Heuristique: si perte > 180px, on considère clavier ouvert
        this.kbOpen.set(this.baseVH - h > 180);
      };
      this.vv.addEventListener('resize', onVv);
      this.vv.addEventListener('scroll', onVv);
    }
  }

  ngOnDestroy(): void {
    if (!this.isBrowser) return;
    if (this.vv) {
      try {
        this.vv.removeEventListener?.('resize', () => { });
        this.vv.removeEventListener?.('scroll', () => { });
      } catch { }
    }
  }
  /** Thème courant (détection simple alignée avec le reste de l’app) */
  private get isDark(): boolean {
    try { return document.body.classList.contains('dark-mode'); } catch { return false; }
  }
  /** Détecte la langue courante via l'URL, sinon localStorage('lang'), sinon 'en' */
  private get currentLang(): string {
    const m = this.router.url.match(/^\/(en|es|fr|pt|it|hi|bn|ta|te|mr|id|vi|tr|km|th|ms|zu|af)(\/|$)/);
    if (m?.[1]) return m[1];
    try {
      const ls = this.isBrowser ? localStorage.getItem('lang') || '' : '';
      if (/^(en|es|fr|pt|it|hi|bn|ta|te|mr|id|vi|tr|km|th|ms|zu|af)$/.test(ls)) return ls;
    } catch { }
    return 'en';
  }

  /** Normalise un routerLink (string | string[]) en string pour isActive */
  private linkToString(link: string | any[]): string {
    if (Array.isArray(link)) return link.join('/').replace(/\/{2,}/g, '/');
    return link;
  }

  /** Construit un routerLink (supporte url fonction pour liens dépendant de la langue) */
  buildLink(it: { url: string | any[] | (() => string | any[]) }) {
    return typeof it.url === 'function' ? it.url() : it.url;
  }

  isActive(url: string | any[]): boolean {
    const link = this.linkToString(url);
    return this.router.isActive(link, {
      paths: 'exact',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored'
    });
  }

  get palette() {
    // Fond + bordure (ring) + shadow + couleurs d’état
    if (this.mode === 'bridge') {
      return this.isDark
        ? {
          bg: 'bg-blue-950/70 backdrop-blur-md',
          ring: 'ring-1 ring-blue-800/40',
          shadow: 'shadow-[0_10px_30px_rgba(0,0,0,0.35)]',
          active: 'text-blue-400',
          idle: 'text-blue-100/70',
          hover: 'hover:text-blue-200'
        }
        : {
          // Light bridge (bleu doux)
          bg: 'bg-gradient-to-b from-[#F0F6FF]/95 to-[#E9F2FF]/95 backdrop-blur-md',
          ring: 'ring-1 ring-[#D7E7FF]',
          shadow: 'shadow-[0_6px_24px_rgba(37,99,235,0.10)]',
          active: 'text-blue-700',
          idle: 'text-slate-700/80',
          hover: 'hover:text-blue-800'
        };
    }
    // mode "default" (violet / lavande)
    return this.isDark
      ? {
        bg: 'bg-violet-950/70 backdrop-blur-md',
        ring: 'ring-1 ring-violet-800/40',
        shadow: 'shadow-[0_10px_30px_rgba(0,0,0,0.35)]',
        active: 'text-violet-400',
        idle: 'text-violet-100/70',
        hover: 'hover:text-violet-200'
      }
      : {
        // Light lavande — aligné avec les cards/header
        bg: 'bg-gradient-to-b from-[#F7F4FF]/95 to-[#EEF0FF]/95 backdrop-blur-md',
        ring: 'ring-1 ring-[#E6E0FF]',
        shadow: 'shadow-[0_6px_24px_rgba(123,97,255,0.12)]',
        active: 'text-purple-700',
        idle: 'text-slate-700/80',
        hover: 'hover:text-purple-800'
      };
  }

  // Items (icônes SVG inline)
  items = [
    { key: 'info', labelKey: 'NAV.INFO', url: '', icon: 'i-info' },
    { key: 'swap', labelKey: 'NAV.SWAP', url: '/swap', icon: 'i-swap' },
    { key: 'bridge', labelKey: 'NAV.BRIDGE', url: '/bridge', icon: 'i-bridge' },
    // Blog doit être préfixé par la langue (ex: /fr/blog)
    { key: 'blog', labelKey: 'NAV.BLOG', url: () => ['/', this.currentLang, 'blog'], icon: 'i-blog' },
    // Paramètres: on ne navigue pas, on émet un event (géré dans le parent)
    { key: 'settings', labelKey: 'NAV.SETTINGS', url: '', icon: 'i-gear' }
  ] as const;
}
